import { useHistory } from "react-router";
import { LoadingScreen } from "components/audience-map/components/LoadingScreen";
import { MapErrorScreen } from "components/audience-map/components/LoadingScreen/MapErrorScreen";
import { walkCarrierRoutePages, routeWalkErrorMessage, routeWalkErrorDetail } from "components/audience-map/routeWalk";
import useOverlaysFetch from "Hooks/useOverlaysFetch.js";
import { MAP_TYPES, CAMPAIGN_INTERNAL_STATUSES, CAMPAIGN_CHANNELS_MIN_RECIPIENTS } from "pages/constants";
import { CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import CONFIG from "config/config";
import { Box, makeStyles, useTheme, useMediaQuery } from "@material-ui/core";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useEffect, useState, useRef, useMemo, useContext } from "react";
import { NextButton } from "components/next-button";
import cn from "classnames";
import { ProgressBarContext } from "components/progress-bar";
import { checkIsNeedUpdateLastActiveStepState } from "utils";
import { generatePath } from "react-router-dom";
import { EDDM_ROUTING } from "config/routing";
import { radiusToZoom } from "./utils";
import { createQuote, updateCampaignExtraData, updateSelfServeCampaignData, updateCampaign } from "../../../../graphQL";
import { styles } from "./styles.js";
import { MAP_EXTRA_STYLES } from "../../options";
import { SummaryBlock } from "./components/SummaryBlock";
import { RouteStatistics } from "./components/RouteStatistics";
import { AddressBlock } from "./components/AddressesBlock";
import { useEDDMCostsCalculation } from "../../../../Hooks/useEDDMCostsCalculation";
import {
  onMapInit,
  renderPolygonsOnEddmMap,
  handleHoverSelectedMissionUpdate,
  getCarrierRoutesWithSuggestions,
} from "../../utils.js";

import { useStore } from "../../../../store";
import { MinimumResidencesModal } from "../../../../components/modals/MinimumResidencesModal";

const useStyles = makeStyles(() => styles);

export const AudienceMapEDDM = ({
  isSubmitted,
  radius,
  setRadius,
  selectedRoutes,
  setSelectedRoutes,
  shouldAutoSave,
  setShouldAutoSave,
}) => {
  const classes = useStyles();
  const {
    country,
    map: { center, zoom, loading, loadingPercentage, loadingError = null, loadingErrorDetail = null },
    campaign: { campaignName, internalStatus, id: campaignId, lastActiveStep, subtype },
    client: { id: clientId, name: clientName },
    updateMap,
    hoveredRoute,
    setHoveredRoute,
    distributionLocations,
    setDistributionLocations,
    setFocusedLocation,
    costsCalculationData,
  } = useStore();
  const { recalculateEDDMCampaignCost } = useEDDMCostsCalculation();
  const { totalCost, tax } = costsCalculationData?.detailedCost || {};

  const focusedLocation = useMemo(
    () => distributionLocations.find((location) => location.isFocused),
    [distributionLocations]
  );

  const drawingMode = null;
  // const [drawingManager, setDrawingManager] = useState(null);
  const [routes, setRoutes] = useState({});
  const [googleMapKey, setGoogleMapKey] = useState(new Date().getTime());
  const [googleMap, setGoogleMap] = useState(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: CONFIG.GOOGLE_API_KEY,
    libraries: ["geometry", "places", "drawing"],
    language: "en",
    version: "3.64",
  });

  const runProgressBar = useContext(ProgressBarContext);
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const clickSaveButtonRef = useRef(false);

  const isNextActive = useMemo(() => {
    return (
      costsCalculationData.flyersAmount &&
      costsCalculationData.flyersAmount >= CAMPAIGN_CHANNELS_MIN_RECIPIENTS[CAMPAIGN_SUBTYPES_ENUM.EDDM]
    );
  }, [costsCalculationData.flyersAmount, isSubmitted]);

  const saveCampaign = async (isAutoSave) => {
    setIsSaving(true);
    if (!isSubmitted && distributionLocations) {
      const payloadCampaignExtraData = {
        campaignId,
        totalCosts: totalCost,
        flyersCount: costsCalculationData?.flyersAmount || 0,
        taxes: tax,
      };

      const isNeedUpdateLastActiveStep = checkIsNeedUpdateLastActiveStepState({
        stateLastActiveStep: lastActiveStep,
        newLastActiveStep: "segments",
        channel: subtype,
      });

      if (isNeedUpdateLastActiveStep) {
        payloadCampaignExtraData.lastActiveStep = "segments";
      }

      await updateCampaignExtraData(payloadCampaignExtraData);
      await updateSelfServeCampaignData({
        campaignId: campaignId,
        distributionLocations: distributionLocations,
      });
      if (isNeedUpdateLastActiveStep && !isAutoSave) {
        updateCampaign({ lastActiveStep: "segments" });
      }
      await setQuoteNumber(campaignId);
      if (!isAutoSave) {
        nextBtnClicked(campaignId);
      }
    }
    setIsSaving(false);
  };

  const setQuoteNumber = async (campaignId) => {
    const quote = await createQuote(campaignId);
    updateCampaign({ quote });
  };

  const headerActions = {
    HOME: {
      action: () => {
        console.log("redirect home");
      },
    },
    NEXT: {
      isActive: true,
      action: async () => {
        if (isSubmitted) {
          runProgressBar(60);
          nextBtnClicked(campaignId);
          runProgressBar(80);
          runProgressBar(-1);
        } else {
          clickSaveButtonRef.current = false;
          await saveCampaign();
        }
      },
      hintPlacement: "bottom",
      hint: "",
    },
  };

  const nextBtnClicked = (id) => {
    // Always navigate to Flyer page (Design + Tracking combined)
    return history.push(generatePath(EDDM_ROUTING.EDDM_FLYER, { campaignId: id, clientId }));
  };

  const mapRef = useRef(null);
  // Last { center, radius } handed to fetchRoutes, so the error state's Retry
  // control can re-run it. Written inside fetchRoutes — the only place
  // `loadingError` is set — so every entry point records what to retry.
  const lastRouteRequestRef = useRef(null);
  // Aborts the walk and the in-flight GraphQL request; wired to the spinner's Cancel.
  const routeAbortRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("768"));

  const geocoder = useMemo(
    () => (window?.google?.maps?.Geocoder ? new window.google.maps.Geocoder() : null),
    [window.google, isLoaded]
  );
  // useEffect(() => {
  //   if (drawingManager) {
  //     drawingManager.setDrawingMode(drawingMode);
  //   }
  // }, [drawingMode, drawingManager]);

  useEffect(() => {
    if (geocoder && googleMap && !Object.values(routes).length) {
      geocoder.geocode({ address: country.name }, (results, status) => {
        if (status === "OK") {
          googleMap.setCenter(results[0].geometry.location);
          updateMap({ zoom: 5 });
          // googleMap.fitBounds(results[0].geometry.viewport);
        }
      });
    }
  }, [geocoder, googleMap, center]);

  useEffect(() => {
    handleHoverSelectedMissionUpdate({ mapInit: googleMap, hoveredMission: hoveredRoute?.id || "" });
  }, [hoveredRoute]);

  useEffect(() => {
    if (Object.values(routes).length) {
      // LOOK FOR ALL THE SELECTED ROUTES IN NON FOCUSED LOCATION
      const selectedRoutesFromNonFocusedLocations = distributionLocations.reduce((acc, curr) => {
        if (curr.isFocused) {
          return acc;
        }
        return [...acc, ...curr.selectedRoutes];
      }, []);

      // LOOK FOR THE SELECTED ROUTES THAT ARE PART OF THE ROUTES FETCHED FOR THE CURRENT FOCUSED LOCATIONS
      // AND REMOVE THE ROUTES THAT ARE ALREADY SELECTED IN SOME OTHER LOCATION
      const selectedRoutesOnCurrentFocusedLocation = Object.values(routes)
        .reduce((acc, curr) => {
          const selectedRoute = selectedRoutes.find((r) => r.id === curr.id);
          if (selectedRoute) {
            acc.push(selectedRoute);
          }
          return acc;
        }, [])
        .filter((r) => !selectedRoutesFromNonFocusedLocations.find((route) => route.id === r.id));

      // SAVE SELECTED ROUTES ON FOCUSED LOCATION AND REMOVE THE UNSELECTED ROUTES FROM OTHER LOCATIONS
      setDistributionLocations(
        distributionLocations.map((l) => {
          if (l.isFocused) {
            return { ...l, selectedRoutes: selectedRoutesOnCurrentFocusedLocation };
          } else {
            return {
              ...l,
              selectedRoutes: l?.selectedRoutes?.filter((r) => selectedRoutes.find((route) => route.id === r.id)) || [],
            };
          }
        })
      );
    }
  }, [selectedRoutes, routes]);

  /**
   * Mobile twin of SegmentsV3's lookup: bounded walk, guaranteed
   * `loading: false`, and an explicit error the user can retry from.
   */
  const fetchRoutes = async ({ center, radius }) => {
    lastRouteRequestRef.current = { center, radius };
    const abortController = typeof AbortController === "function" ? new AbortController() : null;
    routeAbortRef.current = abortController;
    try {
      const result = await walkCarrierRoutePages({
        signal: abortController ? abortController.signal : undefined,
        fetchPage: ({ after, accumulatedSuggestions, signal }) =>
          getCarrierRoutesWithSuggestions({
            after,
            centerSphere: center,
            radius,
            targets: [], // not used on mobile version
            accumulatedSuggestions, // threaded for parity; mobile sends no targets, so pathfinder returns none
            signal,
          }),
        onProgress: ({ percentage }) => updateMap({ loadingPercentage: percentage }),
      });

      const newRoutes = result.routes || {};
      setRoutes(newRoutes);
      updateMap({ loadingError: routeWalkErrorMessage(result), loadingErrorDetail: routeWalkErrorDetail(result) });
      return newRoutes;
    } finally {
      if (routeAbortRef.current === abortController) {
        routeAbortRef.current = null;
      }
      updateMap({ loading: false, loadingPercentage: null });
    }
  };

  /** Abandons the in-flight lookup and hands the map back — see SegmentsV3.cancelFetchRoutes. */
  const cancelRouteLookup = () => {
    const abortController = routeAbortRef.current;
    if (!abortController) {
      updateMap({ loading: false, loadingPercentage: null });
      return;
    }
    abortController.abort();
  };

  const runRouteLookup = (location, currentRadius) => {
    updateMap({
      loading: true,
      loadingError: null,
      loadingErrorDetail: null,
      center: { lat: location.lat, lng: location.lng },
    });
    return fetchRoutes({ center: [location.lng, location.lat], radius: currentRadius * 1609.34 }).catch((error) => {
      console.error("EDDM carrier-route lookup failed:", error);
      updateMap({
        loading: false,
        loadingPercentage: null,
        loadingError: routeWalkErrorMessage({ ok: false }),
        loadingErrorDetail: routeWalkErrorDetail({ ok: false }),
      });
    });
  };

  const retryRouteLookup = () => {
    const lastRequest = lastRouteRequestRef.current;
    if (!lastRequest) {
      updateMap({ loadingError: null, loadingErrorDetail: null });
      return;
    }
    const [lng, lat] = lastRequest.center;
    updateMap({ loading: true, loadingError: null, loadingErrorDetail: null, center: { lat, lng } });
    return fetchRoutes(lastRequest).catch((error) => {
      console.error("EDDM carrier-route lookup failed:", error);
      updateMap({
        loading: false,
        loadingPercentage: null,
        loadingError: routeWalkErrorMessage({ ok: false }),
        loadingErrorDetail: routeWalkErrorDetail({ ok: false }),
      });
    });
  };

  useEffect(() => {
    // SEARCH NEW ROUTES DEPENDING ON SEARCHED LOCATION AND SELECTED RADIUS
    if (focusedLocation && radius) {
      runRouteLookup(focusedLocation, radius);
    }
  }, [radius, focusedLocation?.name]);

  useEffect(() => {
    if (routes && Object.values(routes).length && googleMap) {
      renderPolygonsOnEddmMap({
        googleMap,
        routes,
        handleRouteSelect,
        selectedRoutes,
        isSubmitted,
        setHoveredRoute,
      });
      updateMap({ zoom: radiusToZoom[radius] });
    }
  }, [googleMap]);

  useEffect(() => {
    // RESET GOOGLE MAP ON EVERY ROUTES FETCH
    if (focusedLocation?.selectedRoutes?.length && Object.values(routes).length) {
      const selectedLocationsToDeleteInFocusedLocation = focusedLocation.selectedRoutes.filter(
        (route) => !Object.values(routes).find((r) => r.id === route.id)
      );
      if (selectedLocationsToDeleteInFocusedLocation.length) {
        setSelectedRoutes((selectedRoutes) => {
          return selectedRoutes.filter(
            (route) => !selectedLocationsToDeleteInFocusedLocation.find((r) => r.id === route.id)
          );
        });
      }
    }
    setGoogleMap(null);
    setGoogleMapKey(new Date().getTime());
  }, [routes]);

  useEffect(() => {
    recalculateEDDMCampaignCost();
    if (!distributionLocations?.length) {
      setRoutes({});
    }
  }, [distributionLocations]);

  const handleRouteSelect = ({ routeId, paths, selected, metadata }) => {
    if (isSubmitted) {
      return;
    }
    setShouldAutoSave(true);
    setSelectedRoutes((routes) => {
      const currentMissionIndex = routes.findIndex((r) => r.id === routeId);
      if (selected && currentMissionIndex === -1) {
        return [
          ...routes,
          {
            id: routeId,
            paths,
            type: MAP_TYPES.POLYGON,
            metadata,
          },
        ];
      } else if (!selected && currentMissionIndex !== -1) {
        return [...routes.slice(0, currentMissionIndex), ...routes.slice(currentMissionIndex + 1)];
      } else {
        return routes;
      }
    });
  };

  const onAddressCardClick = (address) => {
    setShouldAutoSave(true);
    setFocusedLocation(address);
    setRadius(address.radius);
  };

  const handleDeleteLocation = (deletedLocation) => {
    setShouldAutoSave(true);
    setDistributionLocations(
      distributionLocations
        .filter((location) => location.name !== deletedLocation.name)
        .map((location, i, array) => {
          if (i + 1 === array.length && deletedLocation.isFocused) {
            setRadius(location.radius);
            return { ...location, isFocused: true };
          } else {
            return location;
          }
        })
    );

    if (deletedLocation?.selectedRoutes?.length) {
      setSelectedRoutes((selectedRoutes) => {
        return selectedRoutes.filter((route) => !deletedLocation?.selectedRoutes.find((r) => r.id === route.id));
      });
    }
  };

  const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);

  const handleClick = () => {
    if (isNextActive) {
      headerActions.NEXT.action();
    } else {
      setIsOpenWarningModal(true);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.mapWrapper} ref={(ref) => (mapRef.current = ref)}>
        {loading && (
          <div className={classes.locationsLoading}>
            <LoadingScreen loadingPercentage={loadingPercentage} onCancel={cancelRouteLookup} />
          </div>
        )}
        {!loading && loadingError && (
          <div className={classes.locationsLoading}>
            <MapErrorScreen message={loadingError} detail={loadingErrorDetail} onRetry={retryRouteLookup} />
          </div>
        )}
        <GoogleMap
          key={googleMapKey}
          onLoad={(map) => {
            onMapInit({ newMap: map, mapInit: googleMap, setMapInit: setGoogleMap });
          }}
          mapContainerStyle={{
            width: "100vw",
            height: isMobile ? "calc(100vh - 80px)" : "calc(100vh - 68px)",
            // height: "calc(100vh - 68px)",
            marginLeft: 0,
          }}
          center={center}
          zoom={zoom}
          options={{
            clickableIcons: false,
            zoomControl: true,
            disableDefaultUI: true,
            keyboardShortcuts: false,
            draggable: !drawingMode,
            styles: MAP_EXTRA_STYLES,
            gestureHandling: "greedy",
          }}
          // onClick={handleMapClick}
        >
          <div className="mob-main-map-container">
            <AddressBlock
              addresses={distributionLocations}
              onAddressCardClick={onAddressCardClick}
              handleDeleteLocation={handleDeleteLocation}
            />
            <SummaryBlock
              selectedRoutes={selectedRoutes}
              campaignName={campaignName}
              isNextActive={isNextActive}
              isModalOpen={isOpenWarningModal}
            />
            <RouteStatistics route={hoveredRoute} selectedRoutes={selectedRoutes} />
            {!!distributionLocations.length &&
              distributionLocations.map((location) => {
                return (
                  <Marker
                    key={location.name}
                    position={{ lng: location.lng, lat: location.lat }}
                    onClick={() => {
                      setShouldAutoSave(true);
                      setFocusedLocation(location);
                      setRadius(location.radius);
                    }}
                  />
                );
              })}
            <Box
              className={cn(classes.nextBtnWrapper, "mob-next-button desk-hidden")}
              style={loading ? { pointerEvents: "none" } : {}}
            >
              <NextButton
                loading={!clickSaveButtonRef.current && isLoading}
                isNextActive={true}
                onClick={handleClick}
              />
            </Box>
          </div>
          {/* <DrawingManager
            onLoad={(loadedDrawingManager) => onDrawingManagerLoad({ loadedDrawingManager, setDrawingManager })}
            drawingMode={drawingMode}
            options={BROAD_AREAS_MAP_OPTIONS}
          /> */}
        </GoogleMap>
      </Box>

      <MinimumResidencesModal
        isOpen={isOpenWarningModal}
        onClose={() => setIsOpenWarningModal(false)}
        flyersAmount={costsCalculationData?.flyersAmount || 0}
      />
    </Box>
  );
};
